import axios from "axios";
import type { MarketInfo, MarketPrice, MarketWithHistory, ApiResponse } from "./types";

const gamma = axios.create({ baseURL: "/api/gamma" });
const clob = axios.create({ baseURL: "/api/clob" });

function extractEndDate(market: any): string {
  const match = market.question?.match(/by\s+(.+?)\s*\?/i);
  if (match) {
    const parsed = new Date(match[1]!);
    if (!isNaN(parsed.getTime())) return parsed.toISOString();
  }
  return market.endDateIso || market.endDate || "";
}

const DATE_PATTERN = /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+\d{1,2}(?:,?\s*\d{4})?\b|\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b|\b\d{4}-\d{2}-\d{2}\b|\b(?:q[1-4]|h[12])\s*\d{4}\b/gi;

export function isByDateEvent(questions: string[]): boolean {
  const templates = questions.map((q) => q.replace(DATE_PATTERN, "<DATE>").trim());
  const counts = new Map<string, number>();
  for (const t of templates) counts.set(t, (counts.get(t) || 0) + 1);
  const maxCount = Math.max(...counts.values());
  return maxCount >= templates.length * 0.5;
}

async function gammaGetEvents(params: Record<string, string>): Promise<any[]> {
  const { data } = await gamma.get("/events", { params });
  return Array.isArray(data) ? data : [];
}

async function fetchPriceHistory(tokenId: string): Promise<MarketPrice[]> {
  try {
    const { data } = await clob.get("/prices-history", {
      params: { market: tokenId, interval: "max", fidelity: 60 },
    });
    const history = Array.isArray(data) ? data : data?.history || [];
    return history.map((point: any) => ({ t: point.t, p: point.p }));
  } catch {
    return [];
  }
}

async function findEventBySlug(slug: string): Promise<{ title: string; markets: MarketInfo[] }> {
  const data = await gammaGetEvents({ slug });
  if (data.length === 0) throw new Error(`Event not found: ${slug}`);

  const event = data[0];
  const markets: MarketInfo[] = [];

  for (const m of event.markets || []) {
    let clobTokenIds: string[] = [];
    try {
      clobTokenIds = JSON.parse(m.clobTokenIds || "[]");
    } catch {}

    if (clobTokenIds.length < 1) continue;

    markets.push({
      conditionId: m.conditionId,
      question: m.question,
      endDate: extractEndDate(m),
      clobTokenIds,
      slug: m.slug || "",
    });
  }

  markets.sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime());

  return { title: event.title || slug, markets };
}

export async function fetchEventData(slug: string): Promise<ApiResponse> {
  const { title, markets } = await findEventBySlug(slug);

  const marketsWithHistory: MarketWithHistory[] = await Promise.all(
    markets.map(async (info): Promise<MarketWithHistory> => {
      const yesTokenId = info.clobTokenIds[0]!;
      const history = await fetchPriceHistory(yesTokenId);
      return { info, history };
    })
  );

  let filtered = marketsWithHistory.filter((m) => m.history.length > 0);

  if (!isByDateEvent(markets.map((m) => m.question))) {
    filtered.sort((a, b) => {
      const priceA = a.history[a.history.length - 1]?.p ?? 0;
      const priceB = b.history[b.history.length - 1]?.p ?? 0;
      return priceA - priceB;
    });
  }

  return { title, markets: filtered, updatedAt: Date.now() };
}

export interface EventSummary {
  slug: string;
  title: string;
  marketCount: number;
}

export async function fetchPopularEvents(): Promise<EventSummary[]> {
  const data = await gammaGetEvents({
    active: "true",
    closed: "false",
    limit: "40",
    order: "volume",
    ascending: "false",
  });

  return data
    .filter((e: any) => e.slug && e.markets && e.markets.length >= 2)
    .map((e: any) => ({
      slug: e.slug as string,
      title: (e.title || e.slug) as string,
      marketCount: e.markets.length as number,
    }));
}
